import { motion } from "framer-motion";
import { Clock, CheckCircle, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getHoursRemaining } from "@/lib/date-utils";

interface PendingResolution {
  id: string;
  event_id: string;
  proposed_outcome: boolean;
  submitted_by: string;
  submitted_at: string;
  status: string;
  dispute_deadline?: string | null;
  dispute_count?: number;
  auto_approved?: boolean;
  escalated?: boolean;
  event?: {
    title: string;
    yes_pool: number;
    no_pool: number;
    is_platform_event: boolean;
  };
  submitter?: {
    username: string | null;
  };
  votes?: {
    agrees: number;
    disputes: number;
  };
}

interface AdminPendingResolutionsProps {
  pendingResolutions: PendingResolution[];
  isLoading: boolean;
  resolutionFilter: "all" | "escalated";
  setResolutionFilter: (filter: "all" | "escalated") => void;
  processingResolutionId: string | null;
  onApprove: (resolution: PendingResolution) => void;
  onReject: (resolutionId: string) => void;
}

const AdminPendingResolutions = ({
  pendingResolutions,
  isLoading,
  resolutionFilter,
  setResolutionFilter,
  processingResolutionId,
  onApprove,
  onReject,
}: AdminPendingResolutionsProps) => {
  const filteredResolutions = pendingResolutions.filter(
    r => resolutionFilter === "all" || r.escalated
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <Clock size={20} className="text-primary" />
          Pending Resolutions ({pendingResolutions.length})
        </h2>
        <div className="flex gap-2">
          <Button
            variant={resolutionFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setResolutionFilter("all")}
          >
            All
          </Button>
          <Button
            variant={resolutionFilter === "escalated" ? "default" : "outline"}
            size="sm"
            onClick={() => setResolutionFilter("escalated")}
            className={resolutionFilter === "escalated" ? "" : "border-amber-500/30 text-amber-500 hover:bg-amber-500/10"}
          >
            <AlertTriangle size={14} className="mr-1" />
            Escalated ({pendingResolutions.filter(r => r.escalated).length})
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : filteredResolutions.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <CheckCircle size={48} className="mx-auto mb-4 text-success/50" />
          <p>No {resolutionFilter === "escalated" ? "escalated" : "pending"} resolutions to review</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredResolutions.map((resolution) => {
            const totalVotes = (resolution.votes?.agrees || 0) + (resolution.votes?.disputes || 0);
            const agreePercent = totalVotes > 0 
              ? ((resolution.votes?.agrees || 0) / totalVotes) * 100 
              : 0;
            const hoursRemaining = resolution.dispute_deadline 
              ? getHoursRemaining(resolution.dispute_deadline) 
              : null;

            return (
              <div
                key={resolution.id}
                className={`p-4 rounded-lg border ${
                  resolution.escalated 
                    ? "bg-amber-500/5 border-amber-500/30" 
                    : "bg-muted/30 border-border/50"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {resolution.escalated && (
                        <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-500 rounded font-medium flex items-center gap-1">
                          <AlertTriangle size={12} />
                          CONTESTED
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                        resolution.proposed_outcome 
                          ? "bg-success/20 text-success" 
                          : "bg-destructive/20 text-destructive"
                      }`}>
                        Proposed: {resolution.proposed_outcome ? "YES" : "NO"}
                      </span>
                      {!resolution.event?.is_platform_event && (
                        <span className="px-2 py-0.5 text-xs bg-accent/20 text-accent rounded">
                          USER EVENT
                        </span>
                      )}
                      {hoursRemaining !== null && !resolution.escalated && (
                        <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded">
                          Auto-approve in {hoursRemaining}h
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-foreground mb-1">
                      {resolution.event?.title || "Unknown Event"}
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      Submitted by: {resolution.submitter?.username || "Unknown"} • {" "}
                      {new Date(resolution.submitted_at).toLocaleString()}
                    </p>
                    
                    {/* Vote breakdown */}
                    {totalVotes > 0 && (
                      <div className="mb-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground">
                            {resolution.votes?.agrees} agree / {resolution.votes?.disputes} dispute ({agreePercent.toFixed(0)}%)
                          </span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden flex">
                          <div
                            className="h-full bg-success transition-all duration-500"
                            style={{ width: `${agreePercent}%` }}
                          />
                          <div
                            className="h-full bg-destructive transition-all duration-500"
                            style={{ width: `${100 - agreePercent}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-4 text-sm">
                      <span className="text-success">
                        YES: {resolution.event?.yes_pool?.toLocaleString() || 0} DC
                      </span>
                      <span className="text-destructive">
                        NO: {resolution.event?.no_pool?.toLocaleString() || 0} DC
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => onApprove(resolution)}
                      disabled={processingResolutionId === resolution.id}
                      title="Approve & Resolve"
                    >
                      {processingResolutionId === resolution.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <CheckCircle size={14} />
                      )}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onReject(resolution.id)}
                      disabled={processingResolutionId === resolution.id}
                      title="Reject"
                    >
                      {processingResolutionId === resolution.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <XCircle size={14} />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default AdminPendingResolutions;
